import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { RGBELoader } from "three/addons/loaders/RGBELoader.js";
import { loadLibraryData, getAlbumHref } from "./library.js";
import "./base.css";
import "./style.css";

const BOOKSHELF_MODEL = "./media/GLTF/Bookshelf.glb";

async function startLanding() {
  const library = await loadLibraryData();
  const sceneHost = document.getElementById("bookshelfScene");
  const landingTitle = document.getElementById("landingTitle");
  const landingText = document.getElementById("landingText");
  const albumLinks = document.getElementById("albumLinks");

  if (landingTitle) {
    landingTitle.textContent = library.desktopLandingTitle;
  }

  if (landingText) {
    landingText.textContent = library.desktopLandingText;
  }

  if (albumLinks) {
    renderAlbumLinks(albumLinks, library.albums);
  }

  if (!sceneHost) return;

  await document.fonts?.ready;

  const viewer = await createBookshelfViewer(sceneHost, library.albums);

  if (albumLinks) {
    albumLinks.querySelectorAll(".album-link").forEach((link, index) => {
      link.addEventListener("mouseenter", () => viewer.setActiveBook(index));
      link.addEventListener("focus", () => viewer.setActiveBook(index));
    });

    viewer.onActiveBookChange = (index) => {
      albumLinks.querySelectorAll(".album-link").forEach((link, linkIndex) => {
        link.classList.toggle("is-active", linkIndex === index);
      });
    };
  }
}

function renderAlbumLinks(container, albums) {
  const fragment = document.createDocumentFragment();

  albums.forEach((album) => {
    const link = document.createElement("a");
    link.className = "album-link";
    link.href = getAlbumHref(album);
    link.textContent = album.title;
    link.setAttribute("aria-label", `Open ${album.title}`);
    fragment.append(link);
  });

  container.replaceChildren(fragment);
}

async function createBookshelfViewer(host, albums) {
  const loading = document.getElementById("sceneLoading");
  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog("#ffffff", 9, 18);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.domElement.className = "bookshelf-canvas";
  host.append(renderer.domElement);

  const camera = new THREE.PerspectiveCamera(50, 1.7777, 0.1, 1000);
  camera.position.set(0, 0, 10);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enabled = false;

  camera.quaternion.setFromEuler(new THREE.Euler(0, 0, 0));
  camera.updateMatrixWorld();

  controls.addEventListener("start", () => {
    renderer.domElement.classList.add("is-dragging");
  });
  controls.addEventListener("end", () => {
    renderer.domElement.classList.remove("is-dragging");
  });

  await addLighting(scene);

  const shelfAnchor = new THREE.Group();
  scene.add(shelfAnchor);

  const bookGroup = new THREE.Group();
  bookGroup.position.set(0, 0, 0);
  scene.add(bookGroup);

  let books = [];

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let hoveredBook = null;
  let activeBookIndex = 0;
  let onActiveBookChange = null;

  const setActiveBook = (index) => {
    activeBookIndex = THREE.MathUtils.clamp(index, 0, books.length - 1);
    updateBookStates(books, activeBookIndex, hoveredBook);
    onActiveBookChange?.(activeBookIndex);
  };

  new GLTFLoader().load(
    BOOKSHELF_MODEL,
    (gltf) => {
      const model = gltf.scene;

      const lightmap = scene.userData.lightmap;
      
      model.traverse((child) => {
        if (!child.isMesh) return;

        const mat = child.material;
        if (!mat) return;

        const geo = child.geometry;

        // Three.js needs UV2 for lightmaps
        if (!geo.attributes.uv2 && geo.attributes.uv) {
          geo.setAttribute(
            "uv2",
            new THREE.BufferAttribute(
              geo.attributes.uv.array,
              2
            )
          );
        }

        mat.lightMap = lightmap;
        mat.lightMapIntensity = 1.5;
        mat.needsUpdate = true;
      });
      
      // Find the book model
      let bookTemplate = null;
      model.traverse((child) => {
        if (child.isMesh && child.name.toLowerCase().includes("book")) {
          bookTemplate = child;
        }
      });
      
      // Create books from template
      if (bookTemplate) {
        books = createAlbumBooks(albums, bookTemplate);
        books.forEach((book) => bookGroup.add(book));
      }
      
      prepareShelfModel(model);
      shelfAnchor.add(model);
      
      setActiveBook(0);
      loading?.classList.add("is-hidden");
    },
    (progress) => {
      // Optional: Handle loading progress
    },
    (error) => {
      console.error("Failed to load bookshelf model:", error);
      loading.textContent = "Shelf model unavailable";
    }
  );

  const resize = () => {
    const { width, height } = host.getBoundingClientRect();
    renderer.setSize(Math.max(width, 1), Math.max(height, 1), false);
    camera.aspect = Math.max(width, 1) / Math.max(height, 1);
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);
  };

  const updatePointer = (event) => {
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  };

  const intersectBook = () => {
    raycaster.setFromCamera(pointer, camera);
    const intersects = raycaster.intersectObjects(books, false);
    return intersects[0]?.object || null;
  };

  renderer.domElement.addEventListener("pointermove", (event) => {
    updatePointer(event);
    hoveredBook = intersectBook();
    renderer.domElement.classList.toggle("is-hovering-book", Boolean(hoveredBook));

    if (hoveredBook) {
      setActiveBook(hoveredBook.userData.index);
    } else {
      updateBookStates(books, activeBookIndex, null);
    }
  });

  renderer.domElement.addEventListener("pointerleave", () => {
    hoveredBook = null;
    renderer.domElement.classList.remove("is-hovering-book");
    updateBookStates(books, activeBookIndex, null);
  });

  renderer.domElement.addEventListener("click", () => {
    if (hoveredBook?.userData.href) {
      window.location.href = hoveredBook.userData.href;
    }
  });

  host.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setActiveBook(activeBookIndex - 1);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      setActiveBook(activeBookIndex + 1);
    }

    if (event.key === "Enter") {
      event.preventDefault();
      window.location.href = books[activeBookIndex].userData.href;
    }
  });

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(host);
  resize();

  function animate() {
    requestAnimationFrame(animate);

    const elapsed = performance.now() * 0.001;
    books.forEach((book, index) => {
      const targetZ = book.userData.isActive ? 0.34 : 0;
      const targetRotation = book.userData.isActive ? -0.04 + Math.PI / 2 : book.userData.baseRotation;
      book.position.z = THREE.MathUtils.lerp(book.position.z, targetZ, 0.08);
      book.rotation.y = THREE.MathUtils.lerp(book.rotation.y, targetRotation, 0.08);
      book.rotation.z = book.userData.baseTilt + Math.sin(elapsed + index) * 0.003;
    });

    renderer.render(scene, camera);
  }

  animate();

  return {
    set onActiveBookChange(callback) {
      onActiveBookChange = callback;
      onActiveBookChange?.(activeBookIndex);
    },
    setActiveBook,
  };
}

async function addLighting(scene) {
  const hdrLoader = new RGBELoader();

  const lightmap = await hdrLoader.loadAsync(
    "./media/GLTF/lightmap.hdr"
  );

  // Treat HDR as texture, NOT environment
  lightmap.mapping = THREE.UVMapping;
  lightmap.colorSpace = THREE.LinearSRGBColorSpace;
  lightmap.flipY = false;

  scene.userData.lightmap = lightmap;

  // small fill light so scene isn't black
  scene.add(new THREE.AmbientLight(0xffffff, 0.15));
}

function prepareShelfModel(model) {
  model.traverse((child) => {
    if (!child.isMesh) return;
    
    model.traverse((child) => {
      if (!child.isMesh) return;

      // Convert non-PBR materials
      if (child.material?.isMeshBasicMaterial) {
        child.material = new THREE.MeshStandardMaterial({
          map: child.material.map,
          roughness: 0.6,
          metalness: 0.0,
        });
      }
    });

    if (child.name === "Book") {
      child.visible = false;
      return;
    }

    if (!child.material) {
      child.material = new THREE.MeshStandardMaterial({
        color: "#7a5235",
        roughness: 0.72,
      });
      return;
    }

    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];

    materials.forEach((material) => {
      material.side = THREE.FrontSide;
      material.needsUpdate = true;
    });
  });
}

function createAlbumBooks(albums, bookTemplate) {
  if (!bookTemplate) return [];
  
  const totalWidth = albums.reduce((sum, _album, index) => {
    return sum + 0.38 + (index % 3) * 0.06;
  }, 0);
  const gap = 0.01;
  let cursor = -(totalWidth + gap * (albums.length - 1)) / 2;

  return albums.map((album, index) => {
    // Clone the book template
    const mesh = bookTemplate.clone(true);
    
    const width = 0.38 + (index % 3) * 0.06;
    const height = 1.85 + (index % 4) * 0.14;
    const x = cursor + width / 2;

    mesh.position.set(x, 1.85 / 2 - 0.92, 0.18 + (index % 2) * 0.02);
    mesh.rotation.y = -0.08 + index * 0.025 + Math.PI / 2;
    mesh.rotation.z = -0.035 + (index % 3) * 0.025;
    
    mesh.userData = {
      album,
      href: getAlbumHref(album),
      index,
      baseRotation: mesh.rotation.y,
      baseTilt: mesh.rotation.z,
      isActive: false,
    };

    cursor += width + gap;
    return mesh;
  });
}

function updateBookStates(books, activeIndex, hoveredBook) {
  books.forEach((book, index) => {
    const isActive = index === activeIndex || book === hoveredBook;
    book.userData.isActive = isActive;
  });
}

startLanding().catch((error) => {
  console.error("Failed to initialize landing page:", error);
});
